import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import StoreList from '../components/admin/StoreList';
import StoreGroupList from '../components/admin/StoreGroupList';
import StoreMetadataEditor from '../components/admin/StoreMetadataEditor';
import AddStoreModal from '../components/admin/AddStoreModal';
import TaxonomyManager from '../components/admin/TaxonomyManager';
import CreateStoreGroupModal from '../components/admin/CreateStoreGroupModal';
import StoreGroupManager from '../components/admin/StoreGroupManager';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function UnifiedClientAdmin() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('stores');
  const [editingStore, setEditingStore] = useState(null);
  const [showAddStore, setShowAddStore] = useState(false);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [managingGroup, setManagingGroup] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    fetchClient();
  }, [clientId]);

  const fetchClient = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/admin/unified-clients/${clientId}`);
      if (!response.ok) {
        throw new Error('Failed to fetch client');
      }
      const data = await response.json();
      setClient(data.client);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStoreEdit = (store) => {
    setEditingStore(store);
  };

  const handleStoreUpdate = async (storeId, updates) => {
    try {
      const response = await fetch(
        `${API_URL}/api/admin/unified-clients/${clientId}/stores/${storeId}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updates)
        }
      );
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to update store');
      }
      
      setEditingStore(null);
      setRefreshKey(k => k + 1);
      await fetchClient();
      return true;
    } catch (err) {
      throw err;
    }
  };

  const handleAddStore = () => {
    setShowAddStore(true);
  };

  const handleStoreAdded = (result) => {
    setShowAddStore(false);
    setRefreshKey(k => k + 1);
    fetchClient();
  };

  const handleGroupCreated = (result) => {
    setShowCreateGroup(false);
    fetchClient();
  };

  const handleManageGroup = (group) => {
    setManagingGroup(group);
  };

  const handleGroupMembershipUpdate = () => {
    fetchClient();
  };

  // Keep managingGroup in sync with client data when it changes
  useEffect(() => {
    if (managingGroup && client) {
      const updatedGroup = client.store_groups?.find(g => g.id === managingGroup.id);
      if (updatedGroup && JSON.stringify(updatedGroup.store_ids) !== JSON.stringify(managingGroup.store_ids)) {
        setManagingGroup(updatedGroup);
      }
    }
  }, [client, managingGroup]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 mb-4">{error}</div>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">
                {client?.name || 'Village'}
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                {client?.stores?.length || 0} stores · {client?.store_groups?.length || 0} groups
              </p>
            </div>
            <button
              onClick={() => navigate(-1)}
              className="px-4 py-2 text-gray-600 hover:text-gray-900"
            >
              Back
            </button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('stores')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'stores'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              data-testid="tab-stores"
            >
              Stores
            </button>
            <button
              onClick={() => setActiveTab('groups')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'groups'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              data-testid="tab-groups"
            >
              Store Groups
            </button>
            <button
              onClick={() => setActiveTab('taxonomy')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'taxonomy'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              data-testid="tab-taxonomy"
            >
              Taxonomy
            </button>
          </nav>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {activeTab === 'stores' && (
          <StoreList
            key={refreshKey}
            clientId={clientId}
            villageCode={client?.village_code}
            storeGroups={client?.store_groups || []}
            onEditStore={handleStoreEdit}
            onAddStore={handleAddStore}
          />
        )}
        {activeTab === 'groups' && (
          <StoreGroupList
            clientId={clientId}
            storeGroups={client?.store_groups || []}
            onCreateGroup={() => setShowCreateGroup(true)}
            onManageGroup={handleManageGroup}
            onGroupDeleted={fetchClient}
          />
        )}
        {activeTab === 'taxonomy' && (
          <TaxonomyManager clientId={clientId} />
        )}
      </main>

      {/* Store Editor Modal */}
      {editingStore && (
        <StoreMetadataEditor
          store={editingStore}
          clientId={clientId}
          onSave={handleStoreUpdate}
          onClose={() => setEditingStore(null)}
        />
      )}

      {/* Add Store Modal */}
      {showAddStore && (
        <AddStoreModal
          clientId={clientId}
          villageCode={client?.village_code}
          storeGroups={client?.store_groups || []}
          onSave={handleStoreAdded}
          onClose={() => setShowAddStore(false)}
        />
      )}

      {/* Create Store Group Modal */}
      {showCreateGroup && (
        <CreateStoreGroupModal
          clientId={clientId}
          onSave={handleGroupCreated}
          onClose={() => setShowCreateGroup(false)}
        />
      )}

      {/* Store Group Manager Modal */}
      {managingGroup && (
        <StoreGroupManager
          clientId={clientId}
          group={managingGroup}
          allStores={client?.stores || []}
          onClose={() => setManagingGroup(null)}
          onUpdate={handleGroupMembershipUpdate}
        />
      )}
    </div>
  );
}
